import React, {useEffect, useMemo, useRef} from 'react';
import parse from 'autosuggest-highlight/parse';
import {Autocomplete} from '@mui/material';
import {Grid, Typography} from '@mui/material';
import throttle from 'lodash/throttle';

const autocompleteService: {current: any} = {current: null};

export interface PlaceType {
  description: string;
  place_id: string; // Add this field
  structured_formatting: {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings: [
      {
        offset: number;
        length: number;
      }
    ];
  };
  terms: {value: string; offset: number}[];
}

type Props = {
  placesAPIs: any;
  initialValue?: string;
  inputComponent: (params: any) => React.ReactNode;
  onSelect: (place: PlaceType | null) => void;
};

export default function PlacesAutocomplete({
  initialValue,
  placesAPIs,
  onSelect,
  inputComponent,
}: Props) {
  const [value, setValue] = React.useState<PlaceType | null>(
    initialValue ? ({description: initialValue} as PlaceType) : null
  );
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<PlaceType[]>([]);
  const sessionToken = useRef<any>(null);

  const fetch = useMemo(
    () =>
      throttle(
        (
          request: {input: string; sessionToken: any},
          callback: (results?: PlaceType[]) => void
        ) => {
          if (!autocompleteService.current) {
            console.error('AutocompleteService is null, cannot fetch predictions');
            callback([]);
            return;
          }
          try {
            // Using the current Places API
            autocompleteService.current.getPlacePredictions(
              {
                ...request,
                // types: ['address'],
                // componentRestrictions: {country: 'us'}, // You can modify this based on your needs
              },
              callback
            );
          } catch (error) {
            console.error('Error fetching predictions:', error);
            callback([]);
          }
        },
        200
      ),
    []
  );

  useEffect(() => {
    let active = true;

    const initializeAutocompleteService = () => {
      if (window.google?.maps?.places) {
        try {
          if (!autocompleteService.current) {
            // Using the current Places API
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
            sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
          }
          return true;
        } catch (err) {
          console.error('Failed to create AutocompleteService:', err);
          return false;
        }
      }
      return false;
    };

    const setupService = () => {
      if (placesAPIs && typeof placesAPIs.AutocompleteService === 'function') {
        try {
          if (!autocompleteService.current) {
            autocompleteService.current = new placesAPIs.AutocompleteService();
            sessionToken.current = new placesAPIs.AutocompleteSessionToken();
          }
        } catch (error) {
          console.error('Error creating autocomplete service from props:', error);
          initializeAutocompleteService();
        }
      } else {
        const success = initializeAutocompleteService();
        if (!success) {
          console.error('Google Places API not available - autocomplete will not work');
        }
      }
    };

    setupService();

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    if (autocompleteService.current) {
      fetch(
        {
          input: inputValue,
          sessionToken: sessionToken.current,
        },
        (results?: PlaceType[]) => {
          if (active) {
            let newOptions = [] as PlaceType[];
            if (value) {
              newOptions = [value];
            }
            if (results) {
              newOptions = [...newOptions, ...results];
            }
            setOptions(newOptions);
          }
        }
      );
    }

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, placesAPIs]);

  return (
    <Autocomplete
      size="small"
      id="location"
      getOptionLabel={option => (typeof option === 'string' ? option : option.description)}
      filterOptions={x => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={(event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        onSelect(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={inputComponent}
      renderOption={(props, option) => {
        if (!option.structured_formatting) return;
        const matches = option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: any) => [match.offset, match.offset + match.length])
        );

        return (
          <li {...props}>
            <Grid item xs>
              {parts.map((part, index) => (
                <span key={index} style={{fontWeight: part.highlight ? 700 : 400}}>
                  {part.text}
                </span>
              ))}
              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </li>
        );
      }}
    />
  );
}
